import React, {createContext, useReducer,} from 'react';
import {jwtDecode} from 'jwt-decode';
import DataStorage from '@Helpers/DataStorage';
import DateTimeHelper from '@Helpers/DateTimeHelper';
import useGetPermissions from '@Services/AuthService/useGetPermissions';

const TOKEN_KEY_NAME = 'token';

const AuthContext = createContext();

const initialState = {
	token: null,
	permissions: [],
	loadData: true,
};

const authReducer = (state, action) => {
	switch (action.type) {
		case 'SET_TOKEN':
			return {
				...state,
				token: action.payload,
			};
		case 'SET_PERMISSIONS':
			return {
				...state,
				permissions: action.payload,
			};
		case 'SET_LOADER':
			return {
				...state,
				loadData: action.payload,
			};
		case 'LOGOUT_SUCCESS':
			return {
				token: null,
				permissions: [],
				loadData: false,
			};
		default:
			return state;
	}
};

// eslint-disable-next-line react/prop-types
const AuthProvider = ({children}) => {
	const [state, dispatch] = useReducer(authReducer, initialState);
	
	const setToken = (token) => {
		DataStorage.setData(TOKEN_KEY_NAME, token);
		dispatch({type: 'SET_TOKEN', payload: token});
	};
	
	const setLoader = (loader) => {
		dispatch({type: 'SET_LOADER', payload: loader});
	};
	
	const { getPermissions } = useGetPermissions({dispatch, setLoader});
	
	const login = async (token, callback) => {
		setToken(token);
		await getPermissions(token);
		if (callback) callback();
	};
	
	const removeSessionTokenFromStorage = () => {
		DataStorage.removeData(TOKEN_KEY_NAME);
	};
	
	const logout = () => {
		document.cookie = 'uid=null; max-age=0; path=/';
		removeSessionTokenFromStorage();
		dispatch({type: 'LOGOUT_SUCCESS'});
	};
	
	const isTokenValid = (token) => {
		try {
			const decoded = jwtDecode(token);
			const timeLife = DateTimeHelper.getTokenTimeLife(decoded.exp);
			return timeLife > 0;
		} catch {
			return false;
		}
	};
	
	const initSession = async () => {
		setLoader(true);
		const token = DataStorage.getData(TOKEN_KEY_NAME);
		if (token && isTokenValid(token)) {
			setToken(token);
			await getPermissions(token);
		} else {
			logout();
		}
		setLoader(false);
	};
	
	const isAuthorized = () => {
		return !!state.token && isTokenValid(state.token);
	};
	
	return (
		<AuthContext.Provider
			value={{
				token: state.token,
				permissions: state.permissions,
				loadData: state.loadData,
				login,
				logout,
				isAuthorized,
				setLoader,
				initSession,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};

export {AuthProvider, AuthContext};